import React, { useMemo } from 'react';
import { MapContainer, TileLayer, Polygon, Popup, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import KkpColorBadge from '../kkp/KkpColorBadge';
import { parseZonePolygon } from '../../utils/secteursMapUtils';

import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const FitBounds = ({ zones }) => {
  const map = useMap();

  React.useEffect(() => {
    const allCoords = zones
      .map((z) => parseZonePolygon(z.polygon))
      .filter(Boolean)
      .flat();

    if (allCoords.length > 0) {
      map.fitBounds(allCoords, { padding: [24, 24] });
    }
  }, [map, zones]);

  return null;
};

const FlyTo = ({ position, zoom = 15 }) => {
  const map = useMap();

  React.useEffect(() => {
    if (position) {
      map.flyTo(position, zoom, { duration: 0.8 });
    }
  }, [map, position, zoom]);

  return null;
};

const SecteursZonesMap = ({ zones = [], markers = [], flyTo = null, height = 520 }) => {
  const polygons = useMemo(
    () =>
      zones
        .map((zone) => ({
          zone,
          coords: parseZonePolygon(zone.polygon),
        }))
        .filter((item) => item.coords),
    [zones]
  );

  return (
    <div className="secteurs-map rounded overflow-hidden border" style={{ height }}>
      <MapContainer
        center={[46.8, 2.5]}
        zoom={6}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds zones={zones} />
        {flyTo && <FlyTo position={flyTo.position} zoom={flyTo.zoom} />}
        {polygons.map(({ zone, coords }) => (
          <Polygon
            key={zone.id}
            positions={coords}
            pathOptions={{
              color: zone.couleur || '#3388ff',
              fillColor: zone.couleur || '#3388ff',
              fillOpacity: 0.35,
              weight: 2,
            }}
          >
            <Popup>
              <strong>{zone.nom}</strong>
              <br />
              <KkpColorBadge color={zone.couleur} vivid>
                {zone.commercial || 'Non attribué'}
              </KkpColorBadge>
              <br />
              <small>{zone.codePostal} — {zone.codeCommune}</small>
            </Popup>
          </Polygon>
        ))}
        {markers.map((marker) => (
          <Marker
            key={marker.id}
            position={[marker.lat, marker.lng]}
          >
            {marker.popup && <Popup>{marker.popup}</Popup>}
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};

export default SecteursZonesMap;
